import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Clock,
  CheckCircle2,
  XCircle,
  LogOut,
  Mail,
  Inbox,
  Loader2,
  HandHeart,
  FileText,
  Pencil,
  Upload,
  Plus,
  Image as ImageIcon,
  Send,
  Trash2,
  CreditCard,
  CalendarCheck,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { getAvatarSrc } from "../lib/avatar";
import { formatDateTime } from "../lib/dateFormat";
import { COUNTRY_PHONE_OPTIONS, INDIA_COUNTRY_OPTION } from "../data/phoneCountries";

const TABS = [
  { k: "all", label: "All Members" },
  { k: "draft", label: "Payment due" },
  { k: "pending", label: "Pending" },
  { k: "approved", label: "Approved" },
  { k: "rejected", label: "Rejected" },
  { k: "welfare", label: "Welfare" },
  { k: "eventRegistrations", label: "Event registrations" },
  { k: "activities", label: "Activities" },
  { k: "contact", label: "Contact requests" },
  { k: "messages", label: "Messages" },
];

const PROFILE_PHOTO_MAX_BYTES = 10 * 1024 * 1024;

export default function AdminDashboard() {
  const { admin, loading: authLoading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("pending");
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [contactReqs, setContactReqs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedMessageIds, setSelectedMessageIds] = useState([]);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name, isBulk }
  const [unreadCount, setUnreadCount] = useState(0);
  const [welfare, setWelfare] = useState([]);
  const [eventRegistrations, setEventRegistrations] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCsvOpen, setBulkCsvOpen] = useState(false);
  const [bulkJson, setBulkJson] = useState("");
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkCsvLoading, setBulkCsvLoading] = useState(false);
  const [avatarUploadingId, setAvatarUploadingId] = useState(null);
  const [approvingAvatarId, setApprovingAvatarId] = useState(null);
  const [activityOpen, setActivityOpen] = useState(false);
  const [activityTitle, setActivityTitle] = useState("");
  const [activityMeta, setActivityMeta] = useState("");
  const [activityTag, setActivityTag] = useState("Forum");
  const [activityBody, setActivityBody] = useState("");
  // Each slot: { type: 'file'|'url', file: File|null, url: string, preview: string }
  const [activitySlots, setActivitySlots] = useState([]);
  const [activitySubmitting, setActivitySubmitting] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [activityEditForm, setActivityEditForm] = useState(null);
  const [activityEditSaving, setActivityEditSaving] = useState(false);
  const [deletingActivity, setDeletingActivity] = useState(null);

  useEffect(() => {
    if (!authLoading && !admin) nav("/admin/login", { replace: true });
  }, [admin, authLoading, nav]);

  const refreshStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch {
      /* ignore */
    }
  }, []);

  const loadTab = useCallback(async () => {
    if (!admin) return;
    setLoading(true);
    try {
      if (tab === "contact") {
        const { data } = await api.get("/admin/contact-requests");
        setContactReqs(data);
      } else if (tab === "messages") {
        const { data } = await api.get("/admin/contact-messages");
        // mark all as read locally
        const updated = data.map((m) => ({ ...m, read: true }));

        setMessages(updated);
        setUnreadCount(0);
      } else if (tab === "welfare") {
        const { data } = await api.get("/admin/welfare");
        setWelfare(data);
      } else if (tab === "eventRegistrations") {
        const { data } = await api.get("/admin/event-registrations");
        setEventRegistrations(Array.isArray(data) ? data : []);
      } else if (tab === "activities") {
        const { data } = await api.get("/admin/activities");
        setActivities(data);
      } else {
        const params = {};
        if (tab !== "all") params.status = tab;
        if (memberSearch.trim()) params.q = memberSearch.trim();
        const { data } = await api.get("/admin/members", { params });
        setMembers(data.items);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [tab, admin, memberSearch]);

  useEffect(() => {
    setSelectedMessageIds([]);
    setSelectedContactIds([]);
  }, [tab]);

  useEffect(() => {
    if (admin) {
      refreshStats();
      loadTab();
    }
  }, [admin, refreshStats, loadTab]);
  useEffect(() => {
    const unread = messages.filter((m) => !m.read).length;
    setUnreadCount(unread);
  }, [messages]);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get("/admin/contact-messages");

        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));

          const newMsgs = data.map((m) => ({
            ...m,
            read: existingIds.has(m.id),
          }));

          const hasNew = newMsgs.some((m) => !existingIds.has(m.id));

          if (hasNew) {
            new Audio("/notification.mp3").play().catch(() => { });
          }

          return newMsgs;
        });
      } catch { }
    }, 8000); // every 8 sec

    return () => clearInterval(interval);
  }, []);

  const approve = async (id) => {
    try {
      const { data } = await api.patch(`/admin/members/${id}/approve`);
      toast.success(
        data?.email_sent
          ? "Approved and email sent to member"
          : "Approved, but Resend email was not sent",
      );
      loadTab();
      refreshStats();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const reject = async (id) => {
    try {
      const { data } = await api.patch(`/admin/members/${id}/reject`);
      toast.success(
        data?.email_sent
          ? "Rejected and email sent to member"
          : "Rejected, but Resend email was not sent",
      );
      loadTab();
      refreshStats();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const resolveReq = async (id) => {
    try {
      await api.patch(`/admin/contact-requests/${id}/resolve`);
      toast.success("Marked resolved");
      loadTab();
      refreshStats();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const bulkResolveContacts = async () => {
    if (selectedContactIds.length === 0) return;
    try {
      await api.post(`/admin/contact-requests/bulk-resolve`, { ids: selectedContactIds });
      toast.success(`${selectedContactIds.length} requests resolved`);
      setSelectedContactIds([]);
      loadTab();
      refreshStats();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const decideWelfare = async (id, decision, note) => {
    try {
      await api.patch(`/admin/welfare/${id}/${decision}`, { note: note || "" });
      toast.success(`Welfare ${decision}`);
      loadTab();
      refreshStats();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const decideActivity = async (id, decision) => {
    try {
      await api.patch(`/admin/activities/${id}/${decision}`);
      toast.success(decision === "approve" ? "Activity approved" : "Activity rejected");
      loadTab();
      refreshStats();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendPaymentReminder = async (id) => {
    try {
      const { data } = await api.post(`/admin/members/${id}/payment-reminder`);
      toast.success(
        data?.email_sent
          ? "Payment reminder sent"
          : "Reminder saved, but email was not sent",
      );
      loadTab();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteActivity = async (id) => {
    try {
      await api.delete(`/admin/activities/${id}`);
      toast.success("Activity deleted");
      setDeletingActivity(null);
      loadTab();
      refreshStats();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteMessage = async (id) => {
    try {
      await api.delete(`/admin/contact-messages/${id}`);
      toast.success("Message deleted");
      setDeleteConfirm(null);
      loadTab();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const bulkDeleteMessages = async () => {
    if (selectedMessageIds.length === 0) return;
    try {
      await api.post(`/admin/contact-messages/bulk-delete`, { ids: selectedMessageIds });
      toast.success(`${selectedMessageIds.length} messages deleted`);
      setDeleteConfirm(null);
      setSelectedMessageIds([]);
      loadTab();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteContact = async (id) => {
    try {
      await api.delete(`/admin/contact-requests/${id}`);
      toast.success("Request deleted");
      setDeleteConfirm(null);
      loadTab();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const bulkDeleteContacts = async () => {
    if (selectedContactIds.length === 0) return;
    try {
      await api.post(`/admin/contact-requests/bulk-delete`, { ids: selectedContactIds });
      toast.success(`${selectedContactIds.length} requests deleted`);
      setDeleteConfirm(null);
      setSelectedContactIds([]);
      loadTab();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openActivityEdit = (activity) => {
    setEditingActivity(activity);
    setActivityEditForm({
      title: activity.title || "",
      meta: activity.meta || "",
      tag: activity.tag || "Member post",
      body: activity.body || "",
    });
  };

  const closeActivityEdit = () => {
    setEditingActivity(null);
    setActivityEditForm(null);
  };

  const saveActivityEdit = async (e) => {
    e.preventDefault();
    if (!editingActivity || !activityEditForm) return;
    setActivityEditSaving(true);
    try {
      await api.patch(`/admin/activities/${editingActivity.id}`, activityEditForm);
      toast.success("Activity updated");
      closeActivityEdit();
      loadTab();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActivityEditSaving(false);
    }
  };

  const openEdit = (member) => {
    const memberCountry =
      COUNTRY_PHONE_OPTIONS.find(
        (option) =>
          option.iso === member.country_code ||
          option.dial === member.phone_country_code ||
          option.country === member.country_name,
      ) || INDIA_COUNTRY_OPTION;
    setEditingMember(member);
    setEditForm({
      name: member.name || "",
      email: member.email || "",
      phone: member.phone || "",
      profession: member.profession || "",
      business_details: member.business_details || "",
      city: member.city || "",
      country_code: member.country_code || memberCountry.iso,
      phone_country_code: member.phone_country_code || memberCountry.dial,
      country_name: member.country_name || memberCountry.country,
      residence_type:
        member.residence_type || (memberCountry.iso === "IN" ? "indian" : "nri"),
    });
  };

  const closeEdit = () => {
    setEditingMember(null);
    setEditForm(null);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editingMember || !editForm) return;
    setSavingEdit(true);
    try {
      const payload = {
        name: editForm.name,
        email: editForm.email,
        phone: editForm.phone,
        profession: editForm.profession,
        business_details: editForm.business_details,
        city: editForm.city,
        country_code: editForm.country_code,
        phone_country_code: editForm.phone_country_code,
        country_name: editForm.country_name,
        residence_type: editForm.residence_type,
      };
      await api.patch(`/admin/members/${editingMember.id}`, payload);
      toast.success("Member updated");
      closeEdit();
      loadTab();
      refreshStats();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingEdit(false);
    }
  };

  const uploadAvatar = async (memberId, file, approveAfterUpload = false) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file.");
      return;
    }
    if (file.size > PROFILE_PHOTO_MAX_BYTES) {
      toast.error("Profile photo must be 10 MB or smaller.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setAvatarUploadingId(memberId);
    try {
      const { data } = await api.post(`/admin/members/${memberId}/avatar`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditingMember((prev) =>
        prev && prev.id === memberId
          ? { ...prev, avatar_path: data.path }
          : prev,
      );
      toast.success("Photo uploaded");
      if (approveAfterUpload) {
        setApprovingAvatarId(memberId);
        await api.patch(`/admin/members/${memberId}/approve`);
        toast.success("Uploaded photo and approved member");
        closeEdit();
        loadTab();
        refreshStats();
      }
      loadTab();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAvatarUploadingId(null);
      setApprovingAvatarId(null);
    }
  };

  const openPdfBlob = async (url, fallbackName = "document.pdf") => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(res.data);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadBlob = async (url, filename = "download") => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const escapeCsvValue = (value) => {
    const str = value === null || value === undefined ? "" : String(value);
    return `"${str.replace(/"/g, '""')}"`;
  };

  const handleExportMembersCsv = () => {
    if (!members.length) {
      toast.error("No member data to export.");
      return;
    }

    const rows = members.map((m) => [
      m.membership_number || "",
      m.name || "",
      m.email || "",
      m.phone || "",
      m.profession || "",
      m.business_name || "",
      m.role || "",
      m.experience || "",
      m.business_description || "",
      m.business_details || "",
      m.residence_type || "",
      m.country_name || "",
      m.country_code || "",
      m.phone_country_code || "",
      m.city || "",
      m.amount ?? "",
      m.membership_status || "",
      m.payment_status || "",
      m.transaction_id || "",
      m.created_at || "",
      m.submitted_at || "",
      m.approved_at || "",
      m.rejected_at || "",
    ]);

    const header = [
      "membership_number",
      "name",
      "email",
      "phone",
      "profession",
      "business_name",
      "role",
      "experience",
      "business_description",
      "business_details",
      "residence_type",
      "country_name",
      "country_code",
      "phone_country_code",
      "city",
      "amount",
      "membership_status",
      "payment_status",
      "transaction_id",
      "created_at",
      "submitted_at",
      "approved_at",
      "rejected_at",
    ];

    const csv = [header, ...rows]
      .map((row) => row.map(escapeCsvValue).join(","))
      .join("\n");

    const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8;" });
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `ospf-members-${tab}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
    toast.success("CSV downloaded");
  };

  function parseBulkPayload(raw) {
    const parsed = JSON.parse(raw);

    if (Array.isArray(parsed)) return parsed;

    if (parsed.items && Array.isArray(parsed.items)) {
      return parsed.items;
    }

    throw new Error("Invalid JSON format");
  }

  const handleBulkImport = async (e) => {
    e.preventDefault();
    if (!bulkJson.trim()) {
      toast.error("Paste member JSON first.");
      return;
    }
    setBulkLoading(true);
    setBulkResult(null);
    try {
      const items = parseBulkPayload(bulkJson);
      const { data } = await api.post("/admin/members/bulk-import", { items });
      setBulkResult(data);
      toast.success(
        `Imported ${data.created} new and updated ${data.updated} members`,
      );
      setBulkJson("");
      setBulkOpen(false);
      loadTab();
      refreshStats();
    } catch (err) {
      toast.error(formatApiError(err) || err.message || "Bulk import failed");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleCsvImport = async (e) => {
    e.preventDefault();

    const file = e.target?.elements?.csv_file?.files?.[0];

    if (!file) {
      toast.error("Choose a CSV file first.");
      return;
    }

    setBulkCsvLoading(true);
    setBulkResult(null);

    try {
      const fd = new FormData();
      fd.append("file", file);

      const { data } = await api.post("/admin/members/bulk-import-csv", fd, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setBulkResult(data);

      if (data.created === 0 && data.updated === 0) {
        toast.error("No members imported. Check CSV columns/data.");
      } else {
        toast.success(
          `CSV Imported: ${data.created} created, ${data.updated} updated`,
        );
      }

      setBulkCsvOpen(false);
      loadTab();
      refreshStats();
    } catch (err) {
      toast.error(formatApiError(err) || "CSV import failed");
    } finally {
      setBulkCsvLoading(false);
    }
  };

  const handleActivitySubmit = async (e) => {
    e.preventDefault();

    if (!activityTitle.trim()) {
      toast.error("Add a title first.");
      return;
    }
    if (!activityBody.trim()) {
      toast.error("Add a short story or update.");
      return;
    }
    const validSlots = activitySlots.filter(
      (s) => (s.type === "file" && s.file) || (s.type === "url" && s.url.trim())
    );
    if (!validSlots.length) {
      toast.error("Add at least one image or image link.");
      return;
    }

    setActivitySubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title", activityTitle.trim());
      fd.append("meta", activityMeta.trim());
      fd.append("body", activityBody.trim());
      fd.append("tag", activityTag.trim() || "Forum");

      const fileSlots = validSlots.filter((s) => s.type === "file");
      const urlSlots = validSlots.filter((s) => s.type === "url");

      fileSlots.forEach((s) => fd.append("images", s.file));
      if (urlSlots.length) {
        fd.append("image_urls", JSON.stringify(urlSlots.map((s) => s.url.trim())));
      }

      await api.post("/admin/activities", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Forum story published");
      setActivityTitle("");
      setActivityMeta("");
      setActivityTag("Forum");
      setActivityBody("");
      setActivitySlots([]);
      setActivityOpen(false);
      loadTab();
      refreshStats();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActivitySubmitting(false);
    }
  };

  if (authLoading || !admin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-cream-deep/40"
      data-testid="admin-dashboard-page"
    >
      {/* Admin header */}
      <header className="bg-white border-b border-black/5">
        <div className="container-px py-5 flex items-center justify-between">
          <div>
            <div className="overline">Admin console</div>

            <div className="font-heading text-xl font-medium text-ink">
              Oswal Sabha Professional Forum
            </div>
          </div>

          <button
            onClick={async () => {
              try {
                await logout();
              } catch (e) { }

              localStorage.removeItem("ospf_admin_token");
              sessionStorage.removeItem("ospf_admin_token");

              window.location.href = "/admin/login";
            }}
            className="btn-ghost"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </header>

      {/* Stats */}
      <section
        className="container-px py-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-4"
        data-testid="admin-stats"
      >
        {[
          {
            i: CreditCard,
            k: "Payment due",
            v: stats?.draft ?? "—",
            tone: "bg-orange-50 text-orange-700",
            tab: "draft",
          },
          {
            i: Clock,
            k: "Pending",
            v: stats?.pending ?? "—",
            tone: "bg-amber-50 text-amber-700",
            tab: "pending",
          },
          {
            i: CheckCircle2,
            k: "Approved",
            v: stats?.approved ?? "—",
            tone: "bg-green-50 text-green-700",
            tab: "approved",
          },
          {
            i: XCircle,
            k: "Rejected",
            v: stats?.rejected ?? "—",
            tone: "bg-red-50 text-red-700",
            tab: "rejected",
          },
          {
            i: HandHeart,
            k: "Welfare",
            v: stats?.welfare_pending ?? "—",
            tone: "bg-rose-50 text-rose-700",
            tab: "welfare",
          },
          {
            i: Send,
            k: "Activities",
            v: stats?.activities_pending ?? "—",
            tone: "bg-violet-50 text-violet-700",
            tab: "activities",
          },
          {
            i: CalendarCheck,
            k: "Events",
            v: stats?.event_registrations ?? "—",
            tone: "bg-emerald-50 text-emerald-700",
            tab: "eventRegistrations",
          },
          {
            i: Inbox,
            k: "Contacts",
            v: stats?.contact_requests_pending ?? "—",
            tone: "bg-sky-50 text-sky-700",
            tab: "contact",
          },
        ].map((s, i) => {
          const I = s.i;
          return (
            <button
              key={i}
              onClick={() => setTab(s.tab)}
              className={`rounded-2xl bg-white p-4 shadow-sm border flex flex-col items-center text-center group transition-all duration-300 hover:-translate-y-1 hover:shadow-md ${tab === s.tab ? "border-brand ring-1 ring-brand/20 bg-brand/[0.02]" : "border-black/[0.04] hover:border-brand/20"}`}
              data-testid={`admin-stat-${i}`}
            >
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center ${s.tone} shrink-0 transition-transform group-hover:scale-110`}
              >
                <I className="w-4 h-4" strokeWidth={2} />
              </div>
              <div className="overline text-[10px] mt-3 uppercase tracking-wider text-ink-muted group-hover:text-brand transition-colors">
                {s.k}
              </div>
              <div className="font-heading text-2xl font-semibold text-ink mt-0.5">
                {s.v}
              </div>
            </button>
          );
        })}
      </section>

      {unreadCount > 0 && (
        <div className="container-px mb-4 sticky top-2 z-40">
          <div className="p-4 md:p-5 rounded-2xl bg-red-100 border border-red-300 shadow-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 animate-pulse">
            {/* LEFT */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center text-lg">
                🔔
              </div>

              <div>
                <div className="text-red-700 font-semibold text-base md:text-lg">
                  {unreadCount} New Message{unreadCount > 1 ? "s" : ""}
                </div>
                <div className="text-sm text-red-600">
                  Someone contacted from website
                </div>
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={() => setTab("messages")}
              className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-full text-sm font-medium shadow w-full sm:w-auto"
            >
              Open Inbox
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <section className="container-px">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div
            className="flex items-center gap-2 bg-white rounded-2xl p-1.5 w-full lg:w-fit border border-black/5 shadow-premium overflow-x-auto no-scrollbar"
            data-testid="admin-tabs"
          >
            {TABS.map((t) => {
              const isMessages = t.k === "messages";

              return (
                <button
                  key={t.k}
                  onClick={() => setTab(t.k)}
                  className={`px-4 sm:px-5 py-2 rounded-xl text-sm font-medium transition flex items-center justify-center gap-2 whitespace-nowrap flex-shrink-0 ${tab === t.k
                      ? "bg-ink text-white"
                      : "text-ink-soft hover:text-ink hover:bg-cream-deep"
                    }`}
                >
                  {t.label}

                  {isMessages && unreadCount > 0 && (
                    <span className="px-2 py-0.5 text-[10px] rounded-full bg-red-500 text-white animate-pulse">
                      {unreadCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            <button
              type="button"
              onClick={() => {
                if (tab === "eventRegistrations") {
                  downloadBlob("/admin/event-registrations.csv", "ospf-event-registrations.csv");
                } else {
                  handleExportMembersCsv();
                }
              }}
              className="px-6 py-2.5 rounded-xl border border-black/10 bg-white text-ink hover:bg-cream-deep text-sm font-medium inline-flex items-center justify-center gap-2 w-full sm:min-w-[180px] transition-all"
              data-testid="admin-export-csv-btn"
            >
              <FileText className="w-4 h-4" />
              {tab === "eventRegistrations" ? "Download event CSV" : "Download CSV"}
            </button>
            {tab !== "eventRegistrations" ? (
              <>
                <button
                  type="button"
                  onClick={() => setBulkOpen(true)}
                  className="px-6 py-2.5 rounded-xl border border-black/10 bg-white text-ink hover:bg-cream-deep text-sm font-medium inline-flex items-center justify-center gap-2 w-full sm:min-w-[180px] transition-all"
                  data-testid="admin-bulk-import-btn"
                >
                  <Upload className="w-4 h-4" />
                  Bulk add
                </button>
                <button
                  type="button"
                  onClick={() => setBulkCsvOpen(true)}
                  className="px-6 py-2.5 rounded-xl border border-black/10 bg-white text-ink hover:bg-cream-deep text-sm font-medium inline-flex items-center justify-center gap-2 w-full sm:min-w-[180px] transition-all"
                  data-testid="admin-bulk-csv-btn"
                >
                  <FileText className="w-4 h-4" />
                  Import CSV
                </button>
                <button
                  type="button"
                  onClick={() => setActivityOpen(true)}
                  className="px-6 py-2.5 rounded-xl bg-ink text-white hover:opacity-90 text-sm font-medium inline-flex items-center justify-center gap-2 w-full sm:min-w-[180px] transition-all"
                  data-testid="admin-add-activity-btn"
                >
                  <Send className="w-4 h-4" />
                  Add activity
                </button>
              </>
            ) : null}
          </div>
        </div>
      </section>

      {tab !== "contact" && tab !== "messages" && tab !== "welfare" && tab !== "activities" && tab !== "eventRegistrations" ? (
        <section className="container-px pt-6">
          <div className="max-w-xl">
            <label className="block text-sm font-medium text-ink-soft mb-2">
              Search by membership ID, name, email, phone, profession, or city
            </label>
            <input
              className="input-premium"
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              placeholder="Search OSPF-2026-00001, Gauransh, gmail, +91..."
              data-testid="admin-member-search-input"
            />
          </div>
        </section>
      ) : null}

      <section className="container-px py-6 pb-24">
        {loading ? (
          <div className="flex items-center gap-2 text-ink-muted">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : tab === "contact" ? (
          <ContactReqsTable
            rows={contactReqs}
            selectedIds={selectedContactIds}
            onToggle={(id) =>
              setSelectedContactIds((prev) =>
                prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
              )
            }
            onSelectAll={(ids) => setSelectedContactIds(ids)}
            onResolve={resolveReq}
            onBulkResolve={bulkResolveContacts}
            onDelete={(req) => setDeleteConfirm({ ...req, isContact: true })}
            onBulkDelete={() => setDeleteConfirm({ isBulk: true, isContact: true })}
          />
        ) : tab === "messages" ? (
          <MessagesTable
            rows={messages}
            selectedIds={selectedMessageIds}
            onToggle={(id) =>
              setSelectedMessageIds((prev) =>
                prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
              )
            }
            onSelectAll={(ids) => setSelectedMessageIds(ids)}
            onDelete={(msg) => setDeleteConfirm(msg)}
            onBulkDelete={() => setDeleteConfirm({ isBulk: true })}
          />
        ) : tab === "welfare" ? (
          <WelfareTable rows={welfare} onDecide={decideWelfare} />
        ) : tab === "eventRegistrations" ? (
          <EventRegistrationsTable
            rows={eventRegistrations}
            onOpenFile={openPdfBlob}
          />
        ) : tab === "activities" ? (
          <ActivitiesModeration
            rows={activities}
            onApprove={(id) => decideActivity(id, "approve")}
            onReject={(id) => decideActivity(id, "reject")}
            onEdit={openActivityEdit}
            onDelete={(activity) => setDeletingActivity(activity)}
          />
        ) : (
          <MembersTable
            rows={members}
            onApprove={approve}
            onReject={reject}
            onEdit={openEdit}
            onOpenProof={openPdfBlob}
            onSendPaymentReminder={sendPaymentReminder}
            status={tab}
          />
        )}
      </section>

      {editingMember && editForm ? (
        <EditMemberModal
          member={editingMember}
          form={editForm}
          setForm={setEditForm}
          onClose={closeEdit}
          onSave={saveEdit}
          saving={savingEdit}
          onAvatarUpload={uploadAvatar}
          avatarUploadingId={avatarUploadingId}
          approvingAvatarId={approvingAvatarId}
        />
      ) : null}

      {bulkOpen ? (
        <BulkImportModal
          bulkJson={bulkJson}
          setBulkJson={setBulkJson}
          onClose={() => setBulkOpen(false)}
          onSubmit={handleBulkImport}
          loading={bulkLoading}
          result={bulkResult}
        />
      ) : null}

      {bulkCsvOpen ? (
        <CsvImportModal
          onClose={() => setBulkCsvOpen(false)}
          onSubmit={handleCsvImport}
          loading={bulkCsvLoading}
          result={bulkResult}
        />
      ) : null}

      {activityOpen ? (
        <ActivityComposerModal
          title={activityTitle}
          setTitle={setActivityTitle}
          meta={activityMeta}
          setMeta={setActivityMeta}
          tag={activityTag}
          setTag={setActivityTag}
          body={activityBody}
          setBody={setActivityBody}
          slots={activitySlots}
          setSlots={setActivitySlots}
          onClose={() => {
            setActivityOpen(false);
            activitySlots.forEach((s) => s.preview && URL.revokeObjectURL(s.preview));
            setActivitySlots([]);
          }}
          onSubmit={handleActivitySubmit}
          loading={activitySubmitting}
        />
      ) : null}

      {editingActivity && activityEditForm ? (
        <EditActivityModal
          activity={editingActivity}
          form={activityEditForm}
          setForm={setActivityEditForm}
          onClose={closeActivityEdit}
          onSave={saveActivityEdit}
          saving={activityEditSaving}
        />
      ) : null}

      {deletingActivity ? (
        <ConfirmDeleteModal
          title="Delete Activity"
          desc={`Are you sure you want to delete "${deletingActivity.title}"? This will permanently remove it from the feed.`}
          onClose={() => setDeletingActivity(null)}
          onConfirm={() => deleteActivity(deletingActivity.id)}
        />
      ) : null}

      {deleteConfirm ? (
        <ConfirmDeleteModal
          title={deleteConfirm.isBulk ? (deleteConfirm.isContact ? "Delete Selected Requests" : "Delete Selected Messages") : (deleteConfirm.isContact ? "Delete Request" : "Delete Message")}
          desc={deleteConfirm.isBulk
            ? `Are you sure you want to delete ${deleteConfirm.isContact ? selectedContactIds.length : selectedMessageIds.length} items? This action cannot be undone.`
            : `Are you sure you want to delete this ${deleteConfirm.isContact ? "request" : "message"} from ${deleteConfirm.name || deleteConfirm.requester_name}?`}
          onClose={() => setDeleteConfirm(null)}
          onConfirm={
            deleteConfirm.isBulk
              ? (deleteConfirm.isContact ? bulkDeleteContacts : bulkDeleteMessages)
              : (deleteConfirm.isContact ? () => deleteContact(deleteConfirm.id) : () => deleteMessage(deleteConfirm.id))
          }
        />
      ) : null}
    </div>
  );
}

function MembersTable({
  rows,
  onApprove,
  onReject,
  onEdit,
  onOpenProof,
  onSendPaymentReminder,
  status,
}) {
  if (rows.length === 0)
    return <EmptyState icon={Users} msg={`No ${status} members.`} />;
  return (
    <div className="overflow-x-auto bg-white rounded-2xl shadow-premium border border-black/5">
      <table className="w-full text-sm" data-testid="admin-members-table">
        <thead className="bg-cream-deep text-ink-soft">
          <tr>
            <Th>Member</Th>
            <Th className="hidden md:table-cell">Profession</Th>
            <Th className="hidden lg:table-cell">Plan</Th>
            <Th className="hidden sm:table-cell">Payment</Th>
            <Th className="hidden xl:table-cell">Submitted</Th>
            <Th align="right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((m) => (
            <tr
              key={m.id}
              className="border-t border-black/5"
              data-testid={`admin-member-row-${m.id}`}
            >
              <Td>
                <div className="flex items-center gap-3">
                  {getAvatarSrc(m) ? (
                    <img
                      src={getAvatarSrc(m)}
                      alt={m.name}
                      className="w-12 h-12 rounded-xl object-contain bg-white border border-black/10 shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-brand/10 text-brand flex items-center justify-center font-heading text-sm font-semibold shrink-0">
                      {m.name
                        .split(" ")
                        .slice(0, 2)
                        .map((p) => p[0])
                        .join("")
                        .toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div className="font-medium text-ink">{m.name}</div>
                    <div className="text-xs text-ink-muted">
                      {m.email} · {m.phone}
                    </div>
                  </div>
                </div>
              </Td>
              <Td className="hidden md:table-cell">
                {m.profession}
                <div className="text-xs text-ink-muted">{m.city || "—"}</div>
              </Td>
              <Td className="hidden lg:table-cell">
                {Number(m.amount) > 0 ? (
                  <>
                    {m.residence_type === "indian" ? "Indian" : "NRI"} · ₹{m.amount}
                  </>
                ) : (
                  <span className="text-ink-muted">Plan pending</span>
                )}
              </Td>
              <Td className="hidden sm:table-cell">
                <div className="space-y-1">
                  <Badge
                    tone={
                      m.membership_status === "approved"
                        ? "green"
                        : m.membership_status === "rejected"
                          ? "red"
                          : "amber"
                    }
                  >
                    {m.membership_status}
                  </Badge>
                  <Badge
                    tone={
                      m.payment_status === "paid"
                        ? "green"
                        : m.payment_status === "proof_submitted"
                          ? "blue"
                          : "amber"
                    }
                  >
                    {m.payment_status}
                  </Badge>
                  {m.transaction_id ? (
                    <div className="text-xs text-ink-muted">
                      Txn: {m.transaction_id}
                    </div>
                  ) : null}
                  {m.membership_number ? (
                    <div className="text-xs text-ink-muted">
                      ID: {m.membership_number}
                    </div>
                  ) : null}
                  {m.payment_proof_path ? (
                    <button
                      type="button"
                      onClick={() => onOpenProof(`/admin/members/${m.id}/payment-proof`)}
                      className="text-xs text-brand hover:underline text-left"
                    >
                      View proof
                    </button>
                  ) : null}
                  <div className="text-xs text-ink-muted pt-1 space-y-1">
                    <div>
                      Form notice:{" "}
                      {m.submission_email_sent_at
                        ? "email sent"
                        : "email pending"}
                      {m.submission_whatsapp_sent_at ? " · WhatsApp sent" : ""}
                    </div>
                    <div>
                      Payment notice:{" "}
                      {m.payment_proof_email_sent_at
                        ? "email sent"
                        : "email pending"}
                      {m.payment_proof_whatsapp_sent_at
                        ? " · WhatsApp sent"
                        : ""}
                    </div>
                    {m.membership_status === "draft" ? (
                      <div>
                        Payment reminder:{" "}
                        {m.payment_reminder_email_sent_at
                          ? "email sent"
                          : "not sent"}
                        {m.payment_reminder_whatsapp_sent_at
                          ? " · WhatsApp sent"
                          : ""}
                      </div>
                    ) : null}
                    {m.membership_status === "approved" ? (
                      <div>
                        Approval notice:{" "}
                        {m.approval_email_sent_at
                          ? "email sent"
                          : "email pending"}
                        {m.approval_whatsapp_sent_at ? " · WhatsApp sent" : ""}
                      </div>
                    ) : null}
                    {m.membership_status === "rejected" ? (
                      <div>
                        Rejection notice:{" "}
                        {m.rejection_email_sent_at
                          ? "email sent"
                          : "email pending"}
                        {m.rejection_whatsapp_sent_at ? " · WhatsApp sent" : ""}
                      </div>
                    ) : null}
                  </div>
                </div>
              </Td>
              <Td className="hidden xl:table-cell whitespace-nowrap">
                {formatDate(
                  m.submitted_at || m.payment_submitted_at || m.created_at,
                  "Not submitted yet",
                )}
              </Td>
              <Td align="right">
                <div className="flex flex-wrap items-center justify-start sm:justify-end gap-2">
                  {status === "draft" ? (
                    <>
                      <span className="px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 text-xs font-medium">
                        Awaiting payment
                      </span>
                      <button
                        type="button"
                        onClick={() => onSendPaymentReminder(m.id)}
                        className="px-3 py-1.5 rounded-full bg-brand text-white hover:opacity-90 text-xs font-medium inline-flex items-center gap-1"
                        data-testid={`payment-reminder-btn-${m.id}`}
                      >
                        <Mail className="w-3.5 h-3.5" />
                        Send reminder
                      </button>
                    </>
                  ) : status !== "approved" && (
                    <button
                      onClick={() => onApprove(m.id)}
                      className="px-3 py-1.5 rounded-full bg-green-600 hover:bg-green-700 text-white text-xs font-medium"
                      data-testid={`approve-btn-${m.id}`}
                    >
                      Approve
                    </button>
                  )}
                  {status !== "rejected" && (
                    <button
                      onClick={() => onReject(m.id)}
                      className="px-3 py-1.5 rounded-full bg-white border border-red-200 text-red-700 hover:bg-red-50 text-xs font-medium"
                      data-testid={`reject-btn-${m.id}`}
                    >
                      Reject
                    </button>
                  )}
                  <button
                    onClick={() => onEdit(m)}
                    className="px-3 py-1.5 rounded-full bg-white border border-black/10 text-ink hover:bg-cream-deep text-xs font-medium inline-flex items-center gap-1"
                    data-testid={`edit-btn-${m.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    Edit
                  </button>
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EventRegistrationsTable({ rows, onOpenFile }) {
  if (rows.length === 0) {
    return <EmptyState icon={CalendarCheck} msg="No event registrations yet." />;
  }

  return (
    <div className="overflow-x-auto bg-white rounded-2xl shadow-premium border border-black/5">
      <table className="w-full text-sm" data-testid="admin-event-registrations-table">
        <thead className="bg-cream-deep text-ink-soft">
          <tr>
            <Th>Participant</Th>
            <Th className="hidden md:table-cell">Event</Th>
            <Th className="hidden lg:table-cell">Fees</Th>
            <Th className="hidden xl:table-cell">Submitted</Th>
            <Th align="right">Files</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-t border-black/5">
              <Td>
                <div>
                  <div className="font-medium text-ink">{row.name}</div>
                  <div className="text-xs text-ink-muted">
                    {row.email} · {row.mobile}
                  </div>
                  {row.transaction_id ? (
                    <div className="text-xs text-ink-muted mt-1">
                      Txn: {row.transaction_id}
                    </div>
                  ) : null}
                </div>
              </Td>
              <Td className="hidden md:table-cell">
                {row.event_title || "New Event"}
                <div className="text-xs text-ink-muted">{row.event_slug}</div>
              </Td>
              <Td className="hidden lg:table-cell">
                {Number(row.fee_amount) > 0 ? `₹${row.fee_amount}` : "No fee"}
              </Td>
              <Td className="hidden xl:table-cell whitespace-nowrap">
                {formatDate(row.created_at)}
              </Td>
              <Td align="right">
                <div className="flex flex-wrap items-center justify-start sm:justify-end gap-2">
                  {row.photo_path ? (
                    <button
                      type="button"
                      onClick={() => onOpenFile(`/admin/event-registrations/${row.id}/photo`)}
                      className="px-3 py-1.5 rounded-full bg-white border border-black/10 text-ink hover:bg-cream-deep text-xs font-medium"
                    >
                      View photo
                    </button>
                  ) : null}
                  {row.payment_proof_path ? (
                    <button
                      type="button"
                      onClick={() => onOpenFile(`/admin/event-registrations/${row.id}/payment-proof`)}
                      className="px-3 py-1.5 rounded-full bg-brand text-white hover:opacity-90 text-xs font-medium"
                    >
                      View payment
                    </button>
                  ) : null}
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ContactReqsTable({
  rows,
  selectedIds,
  onToggle,
  onSelectAll,
  onResolve,
  onBulkResolve,
  onDelete,
  onBulkDelete,
}) {
  if (rows.length === 0)
    return <EmptyState icon={Inbox} msg="No contact requests yet." />;

  const allSelected = rows.length > 0 && selectedIds.length === rows.length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-black/10 text-brand focus:ring-brand cursor-pointer"
            checked={allSelected}
            onChange={(e) => {
              if (e.target.checked) {
                onSelectAll(rows.map((r) => r.id));
              } else {
                onSelectAll([]);
              }
            }}
          />
          <span className="text-sm font-medium text-ink-soft">
            {selectedIds.length > 0
              ? `${selectedIds.length} selected`
              : "Select all"}
          </span>
        </div>

        {selectedIds.length > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={onBulkResolve}
              className="px-4 py-2 rounded-full bg-green-50 text-green-600 hover:bg-green-100 flex items-center gap-2 text-sm font-medium transition-colors"
            >
              <CheckCircle2 className="w-4 h-4" />
              <span className="hidden sm:inline">Resolve {selectedIds.length}</span>
              <span className="sm:hidden">{selectedIds.length}</span>
            </button>
            <button
              onClick={onBulkDelete}
              className="px-4 py-2 rounded-full bg-red-50 text-red-600 hover:bg-red-100 flex items-center gap-2 text-sm font-medium transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              <span className="hidden sm:inline">Delete {selectedIds.length}</span>
              <span className="sm:hidden text-xs">{selectedIds.length}</span>
            </button>
          </div>
        )}
      </div>

      <div className="overflow-hidden bg-white rounded-2xl shadow-premium border border-black/5">
        <table className="w-full text-sm" data-testid="admin-contact-reqs-table">
          <thead className="bg-cream-deep/50 text-ink-soft border-b border-black/5">
            <tr>
              <th className="px-5 py-3 text-left w-10"></th>
              <Th>Requester</Th>
              <Th>Member</Th>
              <Th>Message</Th>
              <Th className="hidden sm:table-cell">When</Th>
              <Th>Status</Th>
              <Th align="right">Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((r) => (
              <tr
                key={r.id}
                className={`group hover:bg-cream-deep/20 transition-colors ${selectedIds.includes(r.id) ? "bg-brand/5" : ""}`}
              >
                <td className="px-5 py-4">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-black/10 text-brand focus:ring-brand cursor-pointer"
                    checked={selectedIds.includes(r.id)}
                    onChange={() => onToggle(r.id)}
                  />
                </td>
                <Td>
                  <div className="font-medium text-ink">{r.requester_name}</div>
                  <div className="text-xs text-ink-muted">
                    {r.requester_email}
                    {r.requester_phone ? ` · ${r.requester_phone}` : ""}
                  </div>
                </Td>
                <Td>{r.member_name}</Td>
                <Td className="max-w-md">
                  <div className="line-clamp-3 text-ink-soft">{r.message}</div>
                </Td>
                <Td className="hidden sm:table-cell text-xs text-ink-muted">
                  {formatDate(r.created_at)}
                </Td>
                <Td>
                  <Badge tone={r.status === "resolved" ? "green" : "amber"}>
                    {r.status}
                  </Badge>
                </Td>
                <Td align="right">
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {r.status !== "resolved" && (
                      <button
                        onClick={() => onResolve(r.id)}
                        className="px-3 py-1.5 rounded-full bg-ink text-white text-[10px] font-medium hover:opacity-90"
                        data-testid={`resolve-req-btn-${r.id}`}
                      >
                        Resolve
                      </button>
                    )}
                    <button
                      onClick={() => onDelete(r)}
                      className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                      title="Delete request"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MessagesTable({
  rows,
  selectedIds,
  onToggle,
  onSelectAll,
  onDelete,
  onBulkDelete,
}) {
  if (rows.length === 0)
    return <EmptyState icon={Mail} msg="No messages yet." />;

  const allSelected = rows.length > 0 && selectedIds.length === rows.length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-black/10 text-brand focus:ring-brand cursor-pointer"
            checked={allSelected}
            onChange={(e) => {
              if (e.target.checked) {
                onSelectAll(rows.map((r) => r.id));
              } else {
                onSelectAll([]);
              }
            }}
          />
          <span className="text-sm font-medium text-ink-soft">
            {selectedIds.length > 0
              ? `${selectedIds.length} selected`
              : "Select all"}
          </span>
        </div>

        {selectedIds.length > 0 && (
          <button
            onClick={() => onBulkDelete({ isBulk: true })}
            className="px-4 py-2 rounded-full bg-red-50 text-red-600 hover:bg-red-100 flex items-center gap-2 text-sm font-medium transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            <span className="hidden sm:inline">Delete {selectedIds.length} messages</span>
            <span className="sm:hidden">{selectedIds.length}</span>
          </button>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-hidden bg-white rounded-2xl shadow-sm border border-black/5">
        <table className="w-full text-sm">
          <thead className="bg-cream-deep/50 text-ink-soft border-b border-black/5">
            <tr>
              <th className="px-5 py-3 text-left w-10"></th>
              <th className="px-5 py-3 text-left font-semibold uppercase tracking-wider text-[11px]">Sender</th>
              <th className="px-5 py-3 text-left font-semibold uppercase tracking-wider text-[11px]">Message Details</th>
              <th className="px-5 py-3 text-left font-semibold uppercase tracking-wider text-[11px]">Received</th>
              <th className="px-5 py-3 text-right font-semibold uppercase tracking-wider text-[11px]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((m) => (
              <tr
                key={m.id}
                className={`group hover:bg-cream-deep/20 transition-colors ${selectedIds.includes(m.id) ? "bg-brand/5" : ""} ${!m.read ? "font-medium" : ""}`}
              >
                <td className="px-5 py-4">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-black/10 text-brand focus:ring-brand cursor-pointer"
                    checked={selectedIds.includes(m.id)}
                    onChange={() => onToggle(m.id)}
                  />
                </td>
                <td className="px-5 py-4">
                  <div className="font-medium text-ink">{m.name}</div>
                  <div className="text-xs text-ink-muted truncate max-w-[180px]">{m.email}</div>
                  {!m.read && (
                    <span className="inline-block w-2 h-2 rounded-full bg-red-500 mt-1 ml-1" title="Unread"></span>
                  )}
                </td>
                <td className="px-5 py-4">
                  <div className="font-semibold text-ink line-clamp-1 mb-1">{m.subject}</div>
                  <div className="text-ink-soft line-clamp-2 text-xs leading-relaxed max-w-xl">
                    {m.message}
                  </div>
                </td>
                <td className="px-5 py-4 whitespace-nowrap text-xs text-ink-muted">
                  {formatDate(m.created_at)}
                </td>
                <td className="px-5 py-4 text-right">
                  <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => onToggle(m.id)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${selectedIds.includes(m.id) ? "bg-brand text-white" : "bg-cream-deep text-ink-soft hover:bg-brand/10 hover:text-brand"}`}
                    >
                      {selectedIds.includes(m.id) ? "Selected" : "Select"}
                    </button>
                    <button
                      onClick={() => onDelete({ id: m.id, name: m.name })}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 text-xs font-medium transition-colors"
                      title="Delete message"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-3">
        {rows.map((m) => (
          <div
            key={m.id}
            className={`p-4 rounded-2xl bg-white border transition-all ${selectedIds.includes(m.id) ? "border-brand ring-1 ring-brand bg-brand/5" : "border-black/5"
              } ${!m.read ? "border-l-4 border-l-red-500" : ""}`}
            onClick={() => onToggle(m.id)}
          >
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-black/10 text-brand focus:ring-brand"
                  checked={selectedIds.includes(m.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    onToggle(m.id);
                  }}
                />
                <div>
                  <div className="font-semibold text-ink text-sm">{m.name}</div>
                  <div className="text-[10px] text-ink-muted">{m.email}</div>
                </div>
              </div>
              <div className="text-[10px] text-ink-muted">{formatDate(m.created_at)}</div>
            </div>

            <div className="text-xs font-medium text-ink mb-1">{m.subject}</div>
            <p className="text-xs text-ink-soft line-clamp-3 mb-4 leading-relaxed">{m.message}</p>

            <div className="flex justify-end gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete({ id: m.id, name: m.name });
                }}
                className="p-2 rounded-lg bg-red-50 text-red-600 text-xs flex items-center gap-1"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WelfareTable({ rows, onDecide }) {
  const [noteMap, setNoteMap] = useState({});
  if (rows.length === 0)
    return <EmptyState icon={HandHeart} msg="No welfare applications yet." />;
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 gap-4"
      data-testid="admin-welfare-grid"
    >
      {rows.map((w) => (
        <article
          key={w.id}
          className="card-premium"
          data-testid={`admin-welfare-row-${w.id}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="overline">{w.category}</div>
              <h3 className="font-heading text-xl text-ink mt-1">{w.title}</h3>
              <div className="text-sm text-ink-muted mt-1">
                by {w.member_name}
              </div>
            </div>
            <Badge
              tone={
                w.status === "approved"
                  ? "green"
                  : w.status === "rejected"
                    ? "red"
                    : "amber"
              }
            >
              {w.status}
            </Badge>
          </div>
          <div className="mt-4 font-heading text-2xl text-brand">
            ₹{w.amount.toLocaleString("en-IN")}
          </div>
          <p className="mt-3 text-sm text-ink-soft leading-relaxed">
            {w.description}
          </p>
          {w.document_path && (
            <button
              type="button"
              onClick={() =>
                downloadBlob(
                  `/files/welfare/${w.id}`,
                  w.document_path.split("/").pop() || `${w.id}.pdf`,
                )
              }
              className="inline-flex items-center gap-1 text-brand text-sm mt-3 hover:underline"
              data-testid={`admin-welfare-doc-${w.id}`}
            >
              <FileText className="w-4 h-4" /> View supporting document
            </button>
          )}
          {w.admin_note && (
            <div className="mt-3 p-3 rounded-xl bg-cream-deep text-sm">
              <div className="overline mb-1">Admin note</div>
              <div className="text-ink-soft">{w.admin_note}</div>
            </div>
          )}
          {w.status === "pending" && (
            <div className="mt-4 space-y-2">
              <textarea
                className="input-premium min-h-[70px] text-sm"
                placeholder="Optional note to the member"
                value={noteMap[w.id] || ""}
                onChange={(e) =>
                  setNoteMap((m) => ({ ...m, [w.id]: e.target.value }))
                }
                data-testid={`welfare-admin-note-${w.id}`}
              />
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => onDecide(w.id, "reject", noteMap[w.id])}
                  className="px-4 py-2 rounded-full bg-white border border-red-200 text-red-700 hover:bg-red-50 text-sm font-medium"
                  data-testid={`welfare-reject-btn-${w.id}`}
                >
                  Reject
                </button>
                <button
                  onClick={() => onDecide(w.id, "approve", noteMap[w.id])}
                  className="px-4 py-2 rounded-full bg-green-600 hover:bg-green-700 text-white text-sm font-medium"
                  data-testid={`welfare-approve-btn-${w.id}`}
                >
                  Approve
                </button>
              </div>
            </div>
          )}
          <div className="mt-3 text-xs text-ink-muted">
            {formatDate(w.created_at)}
          </div>
        </article>
      ))}
    </div>
  );
}

function ActivitiesModeration({ rows, onApprove, onReject, onEdit, onDelete }) {
  const [filter, setFilter] = useState("pending");
  const filtered =
    filter === "all" ? rows : rows.filter((row) => (row.status || "published") === filter);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {["pending", "published", "rejected", "all"].map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={`px-4 py-2 rounded-full text-sm font-medium border ${filter === key
                ? "bg-ink text-white border-ink"
                : "bg-white text-ink-soft border-black/10 hover:text-ink"
              }`}
          >
            {key === "published" ? "Approved" : key[0].toUpperCase() + key.slice(1)}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Send} msg={`No ${filter} activity posts.`} />
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="admin-activities-grid">
          {filtered.map((activity) => (
            <article key={activity.id} className="card-premium" data-testid={`admin-activity-${activity.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      tone={
                        activity.status === "published"
                          ? "green"
                          : activity.status === "rejected"
                            ? "red"
                            : "amber"
                      }
                    >
                      {activity.status === "published" ? "approved" : activity.status}
                    </Badge>
                    <span className="text-xs text-ink-muted">{activity.tag || "Member post"}</span>
                  </div>
                  <h3 className="mt-3 font-heading text-xl text-ink break-words">{activity.title}</h3>
                  <div className="mt-1 text-xs text-ink-muted">
                    By {activity.member_name}
                    {activity.member_membership_number ? ` · ${activity.member_membership_number}` : ""}
                  </div>
                </div>
                <div className="text-xs text-ink-muted shrink-0">{formatDate(activity.created_at)}</div>
              </div>

              {activity.meta ? <div className="mt-3 text-sm text-ink-muted">{activity.meta}</div> : null}
              <p className="mt-3 text-sm text-ink-soft leading-relaxed whitespace-pre-wrap break-words">
                {activity.body}
              </p>

              {/* Multi-image gallery slider */}
              {activity.admin_gallery_srcs?.length > 1 ? (
                <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
                  {activity.admin_gallery_srcs.map((src, i) => (
                    <img
                      key={i}
                      src={src}
                      alt={`${activity.title} ${i + 1}`}
                      className="h-36 w-36 shrink-0 rounded-xl object-cover border border-black/5"
                    />
                  ))}
                </div>
              ) : activity.admin_image_src ? (
                <img
                  src={activity.admin_image_src}
                  alt={activity.title}
                  className="mt-4 h-52 w-full rounded-2xl object-cover border border-black/5"
                />
              ) : activity.has_gallery ? (
                <div className="mt-4 rounded-2xl bg-cream-deep p-4 text-sm text-ink-muted">
                  {activity.gallery_count} gallery image{activity.gallery_count === 1 ? "" : "s"} attached
                </div>
              ) : null}



              <div className="mt-5 flex flex-wrap justify-end gap-2">
                {activity.status !== "published" ? (
                  <button
                    type="button"
                    onClick={() => onApprove(activity.id)}
                    className="px-3 py-1.5 rounded-full bg-green-600 hover:bg-green-700 text-white text-xs font-medium"
                  >
                    Approve
                  </button>
                ) : null}
                {activity.status !== "rejected" ? (
                  <button
                    type="button"
                    onClick={() => onReject(activity.id)}
                    className="px-3 py-1.5 rounded-full bg-white border border-red-200 text-red-700 hover:bg-red-50 text-xs font-medium"
                  >
                    Reject
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => onEdit(activity)}
                  className="px-3 py-1.5 rounded-full bg-white border border-black/10 text-ink hover:bg-cream-deep text-xs font-medium inline-flex items-center gap-1"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(activity)}
                  className="px-3 py-1.5 rounded-full bg-ink text-white hover:opacity-90 text-xs font-medium inline-flex items-center gap-1"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState({ icon: Icon, msg }) {
  return (
    <div className="card-premium">
      <div className="mx-auto w-12 h-12 rounded-2xl bg-cream-deep text-ink-muted flex items-center justify-center mb-4">
        <Icon className="w-5 h-5" strokeWidth={1.5} />
      </div>
      <div className="text-ink-soft">{msg}</div>
    </div>
  );
}

function Th({ children, align }) {
  return (
    <th
      className={`px-5 py-3 text-${align || "left"} text-xs font-semibold uppercase tracking-wider`}
    >
      {children}
    </th>
  );
}
function Td({ children, align, className = "" }) {
  return (
    <td className={`px-5 py-4 text-${align || "left"} align-top ${className}`}>
      {children}
    </td>
  );
}
function Badge({ children, tone }) {
  const tones = {
    green: "bg-green-50 text-green-700",
    amber: "bg-amber-50 text-amber-700",
    blue: "bg-sky-50 text-sky-700",
    red: "bg-red-50 text-red-700",
  };
  return (
    <span
      className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${tones[tone] || "bg-black/5 text-ink"}`}
    >
      {children}
    </span>
  );
}

function EditMemberModal({
  member,
  form,
  setForm,
  onClose,
  onSave,
  saving,
  onAvatarUpload,
  avatarUploadingId,
  approvingAvatarId,
}) {
  const fileRef = useRef(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const update = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const selectedCountry =
    COUNTRY_PHONE_OPTIONS.find((option) => option.iso === form.country_code) ||
    INDIA_COUNTRY_OPTION;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl border border-black/10 overflow-hidden">
        <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
          <div>
            <div className="overline">Edit member</div>
            <div className="font-heading text-xl text-ink">{member.name}</div>
          </div>
          <button onClick={onClose} className="btn-ghost" type="button">
            Close
          </button>
        </div>
        <form onSubmit={onSave} className="p-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            {getAvatarSrc(member) ? (
              <img
                src={getAvatarSrc(member)}
                alt={member.name}
                className="w-20 h-20 rounded-2xl object-contain bg-white border border-black/10"
              />
            ) : (
              <div className="w-20 h-20 rounded-2xl bg-brand/10 text-brand flex items-center justify-center font-heading text-xl font-semibold">
                {member.name
                  .split(" ")
                  .slice(0, 2)
                  .map((p) => p[0])
                  .join("")
                  .toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-ink">
                Professional photo
              </div>
              <div className="text-xs text-ink-muted">
                Upload any image up to 10 MB. The full image will show in the
                directory, member login, and profile after approval.
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={async () => {
                  if (!selectedPhoto) {
                    fileRef.current?.click();
                    return;
                  }
                  await onAvatarUpload(member.id, selectedPhoto);
                  setSelectedPhoto(null);
                }}
                className="btn-secondary w-full sm:w-auto"
                disabled={avatarUploadingId === member.id}
              >
                {avatarUploadingId === member.id
                  ? "Uploading…"
                  : "Replace photo"}
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!selectedPhoto) {
                    fileRef.current?.click();
                    return;
                  }
                  await onAvatarUpload(member.id, selectedPhoto, true);
                  setSelectedPhoto(null);
                }}
                className="btn-primary w-full sm:w-auto"
                disabled={
                  avatarUploadingId === member.id ||
                  approvingAvatarId === member.id
                }
              >
                {approvingAvatarId === member.id
                  ? "Approving…"
                  : "Approve with selected photo"}
              </button>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setSelectedPhoto(file);
                e.target.value = "";
              }}
            />
          </div>
          {selectedPhoto ? (
            <div className="text-xs text-ink-muted">
              Selected photo: {selectedPhoto.name}
            </div>
          ) : null}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              className="input-premium"
              value={form.name}
              onChange={update("name")}
              placeholder="Full name"
            />
            <input
              className="input-premium"
              value={form.email}
              onChange={update("email")}
              placeholder="Email"
            />
            <input
              className="input-premium"
              value={form.phone}
              onChange={update("phone")}
              placeholder="Phone"
            />
            <input
              className="input-premium"
              value={form.profession}
              onChange={update("profession")}
              placeholder="Profession"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-soft mb-2">
              Country / residence
            </label>
            <select
              className="input-premium"
              value={selectedCountry.iso}
              onChange={(e) => {
                const next =
                  COUNTRY_PHONE_OPTIONS.find((option) => option.iso === e.target.value) ||
                  INDIA_COUNTRY_OPTION;
                setForm((prev) => ({
                  ...prev,
                  country_code: next.iso,
                  phone_country_code: next.dial,
                  country_name: next.country,
                  residence_type: next.iso === "IN" ? "indian" : "nri",
                }));
              }}
            >
              {COUNTRY_PHONE_OPTIONS.map((option) => (
                <option key={option.iso} value={option.iso}>
                  {option.country} · {option.dial} ·{" "}
                  {option.iso === "IN" ? "India" : "NRI"}
                </option>
              ))}
            </select>
          </div>
          <input
            className="input-premium"
            value={form.city}
            onChange={update("city")}
            placeholder="City"
          />
          <textarea
            className="input-premium min-h-[120px]"
            value={form.business_details}
            onChange={update("business_details")}
            placeholder="Business / practice details"
          />
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost w-full sm:w-auto">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-primary w-full sm:w-auto">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Saving…
                </>
              ) : (
                "Save changes"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditActivityModal({
  activity,
  form,
  setForm,
  onClose,
  onSave,
  saving,
}) {
  const update = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white w-full max-w-3xl rounded-3xl shadow-2xl border border-black/10 overflow-hidden">
        <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
          <div>
            <div className="overline">Edit activity</div>
            <div className="font-heading text-xl text-ink">{activity.title}</div>
          </div>
          <button onClick={onClose} className="btn-ghost" type="button">
            Close
          </button>
        </div>
        <form onSubmit={onSave} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              className="input-premium"
              value={form.title}
              onChange={update("title")}
              placeholder="Title"
            />
            <input
              className="input-premium"
              value={form.tag}
              onChange={update("tag")}
              placeholder="Tag"
            />
          </div>
          <input
            className="input-premium"
            value={form.meta}
            onChange={update("meta")}
            placeholder="Short subtitle"
          />
          <textarea
            className="input-premium min-h-[180px]"
            value={form.body}
            onChange={update("body")}
            placeholder="Post body"
          />
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost w-full sm:w-auto">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-primary w-full sm:w-auto">
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConfirmDeleteModal({ title, desc, onConfirm, onClose }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 animate-in fade-in zoom-in duration-200 border border-black/5">
        <div className="w-16 h-16 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-6">
          <Trash2 className="w-8 h-8" />
        </div>
        <h3 className="font-heading text-2xl font-light text-ink text-center mb-2">{title}</h3>
        <p className="text-ink-soft text-center leading-relaxed mb-8">{desc || "This action cannot be undone. Are you sure you want to proceed?"}</p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 rounded-2xl border border-black/10 text-ink hover:bg-cream-deep font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-6 py-3 rounded-2xl bg-red-600 text-white hover:bg-red-700 font-medium transition-colors flex items-center justify-center gap-2"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkImportModal({
  bulkJson,
  setBulkJson,
  onClose,
  onSubmit,
  loading,
  result,
}) {
  const sample = `[
  {
    "name": "Amit Jain",
    "email": "amit@example.com",
    "phone": "9876543210",
    "profession": "CA",
    "business_details": "Cash paid offline. Added by admin.",
    "city": "Udaipur",
    "residence_type": "indian",
    "membership_status": "approved",
    "payment_status": "paid"
  }
]`;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white w-full max-w-3xl rounded-3xl shadow-2xl border border-black/10 overflow-hidden">
        <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
          <div>
            <div className="overline">Bulk add members</div>
            <div className="font-heading text-xl text-ink">
              Paste JSON for offline imports
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost" type="button">
            Close
          </button>
        </div>
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <div className="rounded-2xl border border-brand/15 bg-brand/5 p-4 text-sm text-ink-soft">
            Paste a JSON array of members or an object with an{" "}
            <code>items</code> array. Approved and paid are the defaults for
            cash entries.
          </div>
          <textarea
            className="input-premium min-h-[320px] font-mono text-sm"
            value={bulkJson}
            onChange={(e) => setBulkJson(e.target.value)}
            placeholder={sample}
            data-testid="admin-bulk-json-input"
          />
          {result ? (
            <div className="rounded-2xl border border-black/5 bg-cream-deep p-4 text-sm">
              <div className="font-medium text-ink mb-2">
                Last import result
              </div>
              <div className="text-ink-soft">
                Created: {result.created} · Updated: {result.updated} · Errors:{" "}
                {result.errors?.length || 0}
              </div>
            </div>
          ) : null}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setBulkJson(sample)}
              className="btn-ghost inline-flex items-center justify-center gap-2 w-full sm:w-auto"
            >
              <Plus className="w-4 h-4" />
              Load sample
            </button>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <button type="button" onClick={onClose} className="btn-ghost w-full sm:w-auto">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-primary w-full sm:w-auto">
                {loading ? "Importing…" : "Import members"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function CsvImportModal({ onClose, onSubmit, loading, result }) {
  const csvSample = `name,email,phone,profession,business_details,city,residence_type,membership_status,payment_status\nAmit Jain,amit@example.com,9876543210,CA,Cash paid offline,Udaipur,indian,approved,paid`;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl border border-black/10 overflow-hidden">
        <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
          <div>
            <div className="overline">CSV import</div>
            <div className="font-heading text-xl text-ink">
              Upload spreadsheet-style member data
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost" type="button">
            Close
          </button>
        </div>
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <div className="rounded-2xl border border-brand/15 bg-brand/5 p-4 text-sm text-ink-soft">
            Keep photos separate. CSV is for text fields only. Use the edit
            modal to upload each professional photo after import.
          </div>
          <input
            type="file"
            name="csv_file"
            accept=".csv,text/csv"
            className="input-premium"
            data-testid="admin-csv-file-input"
          />
          <div className="rounded-2xl bg-cream-deep p-4 text-xs text-ink-muted whitespace-pre-wrap overflow-auto max-h-56">
            {csvSample}
          </div>
          {result ? (
            <div className="rounded-2xl border border-black/5 bg-cream-deep p-4 text-sm">
              <div className="font-medium text-ink mb-2">
                Last import result
              </div>
              <div className="text-ink-soft">
                Created: {result.created} · Updated: {result.updated} · Errors:{" "}
                {result.errors?.length || 0}
              </div>
            </div>
          ) : null}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
            <button type="button" onClick={onClose} className="btn-ghost w-full sm:w-auto">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn-primary w-full sm:w-auto">
              {loading ? "Importing…" : "Import CSV"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ActivityComposerModal({
  title,
  setTitle,
  meta,
  setMeta,
  tag,
  setTag,
  body,
  setBody,
  slots,
  setSlots,
  onClose,
  onSubmit,
  loading,
}) {
  const MAX_SLOTS = 10;
  const fileInputRef = useState(null);
  const inputRef = fileInputRef[0] !== null ? { current: null } : { current: null };
  // Use a proper ref
  const pickerRef = useState(() => ({ current: null }))[0];

  const addFileSlots = (files) => {
    const remaining = MAX_SLOTS - slots.length;
    if (remaining <= 0) return;
    const toAdd = Array.from(files).slice(0, remaining);
    const newSlots = toAdd.map((f) => ({
      type: "file",
      file: f,
      url: "",
      preview: URL.createObjectURL(f),
    }));
    setSlots((prev) => [...prev, ...newSlots]);
  };

  const addUrlSlot = () => {
    if (slots.length >= MAX_SLOTS) return;
    setSlots((prev) => [...prev, { type: "url", file: null, url: "", preview: "" }]);
  };

  const removeSlot = (idx) => {
    setSlots((prev) => {
      if (prev[idx]?.preview) URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const updateUrlSlot = (idx, val) => {
    setSlots((prev) => prev.map((s, i) => (i === idx ? { ...s, url: val } : s)));
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white w-full max-w-4xl rounded-3xl shadow-2xl border border-black/10 overflow-hidden my-8">
        <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
          <div>
            <div className="overline">Forum activity</div>
            <div className="font-heading text-xl text-ink">Publish a gallery story</div>
          </div>
          <button onClick={onClose} className="btn-ghost" type="button">Close</button>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-5">
          <div className="rounded-2xl border border-brand/15 bg-brand/5 p-4 text-sm text-ink-soft">
            Add title, intro, story and up to {MAX_SLOTS} images — from <strong>local files</strong> or <strong>Google Drive / web links</strong>.
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              className="input-premium"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Activity title"
            />
            <input
              className="input-premium"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder="Tag e.g. Summit, Meetup, Seva"
            />
          </div>

          <input
            className="input-premium"
            value={meta}
            onChange={(e) => setMeta(e.target.value)}
            placeholder="Short subtitle or location line"
          />

          <textarea
            className="input-premium min-h-[120px]"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write the full activity note..."
          />

          {/* ── Image slots ── */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-ink-soft">
                Images
                <span className="ml-1.5 text-xs font-normal text-ink-muted">({slots.length}/{MAX_SLOTS})</span>
              </span>
              {slots.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    slots.forEach((s) => s.preview && URL.revokeObjectURL(s.preview));
                    setSlots([]);
                  }}
                  className="text-xs text-red-500 hover:text-red-700 transition"
                >
                  Clear all
                </button>
              )}
            </div>

            {/* Slot grid */}
            {slots.length > 0 && (
              <div className="space-y-2 mb-3">
                {slots.map((slot, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 rounded-2xl border border-black/8 bg-cream-deep/60 p-3"
                  >
                    {/* Preview thumbnail */}
                    {slot.type === "file" && slot.preview ? (
                      <img
                        src={slot.preview}
                        alt="preview"
                        className="w-12 h-12 rounded-xl object-cover shrink-0 border border-black/8"
                      />
                    ) : slot.type === "url" && slot.url ? (
                      <img
                        src={slot.url.includes("drive.google.com/file") ?
                          slot.url.replace(
                            /drive\.google\.com\/file\/d\/([^/?#]+).*/,
                            "drive.google.com/thumbnail?id=$1&sz=w80"
                          ) : slot.url}
                        alt="url preview"
                        className="w-12 h-12 rounded-xl object-cover shrink-0 border border-black/8 bg-cream-deep"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-xl bg-cream-deep shrink-0 flex items-center justify-center">
                        <ImageIcon className="w-5 h-5 text-ink-muted" />
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      {slot.type === "file" ? (
                        <span className="text-sm text-ink-soft truncate block">{slot.file?.name}</span>
                      ) : (
                        <input
                          type="url"
                          className="input-premium h-9 text-sm px-3 w-full"
                          placeholder="Paste Google Drive or web image URL"
                          value={slot.url}
                          onChange={(e) => updateUrlSlot(idx, e.target.value)}
                        />
                      )}
                    </div>

                    <div className="text-xs text-ink-muted shrink-0 px-1">{idx + 1}</div>

                    <button
                      type="button"
                      onClick={() => removeSlot(idx)}
                      className="shrink-0 rounded-full p-1 hover:bg-red-50 text-red-400 hover:text-red-600 transition"
                      aria-label="Remove"
                    >
                      <Plus className="w-4 h-4 rotate-45" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add buttons */}
            {slots.length < MAX_SLOTS && (
              <div className="flex flex-wrap gap-2">
                <label className="cursor-pointer inline-flex items-center gap-2 rounded-full border-2 border-dashed border-black/15 bg-cream-deep/60 hover:bg-cream-deep hover:border-brand/30 transition px-4 py-2 text-sm text-ink-muted hover:text-brand">
                  <Upload className="w-4 h-4" />
                  Add local photos
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      addFileSlots(e.target.files);
                      e.target.value = "";
                    }}
                  />
                </label>
                <button
                  type="button"
                  onClick={addUrlSlot}
                  className="inline-flex items-center gap-2 rounded-full border-2 border-dashed border-black/15 bg-cream-deep/60 hover:bg-cream-deep hover:border-brand/30 transition px-4 py-2 text-sm text-ink-muted hover:text-brand"
                >
                  <ImageIcon className="w-4 h-4" />
                  Add Drive / web link
                </button>
              </div>
            )}

            <p className="mt-2 text-xs text-ink-muted">
              Google Drive link auto-converts · Any image URL works · Max 5 MB per local file
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost w-full sm:w-auto">Cancel</button>
            <button type="submit" disabled={loading} className="btn-primary w-full sm:w-auto">
              {loading ? "Publishing…" : "Publish story"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
function formatDate(iso, fallback = "—") {
  return formatDateTime(iso, fallback);
}
